import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatVND } from "@/lib/money";
import { Wallet, Search, Plus, Minus, X } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [-100000, -50000, -10000, 10000, 50000, 100000, 200000, 500000];
const TYPES = [
  { v: "deposit", l: "Nạp tiền" },
  { v: "withdraw", l: "Rút tiền" },
  { v: "payment", l: "Thanh toán" },
  { v: "reward", l: "Thưởng" },
  { v: "refund", l: "Hoàn tiền" },
  { v: "adjustment", l: "Điều chỉnh" },
];

export default function AdminWallet() {
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState("");
  const [adjusting, setAdjusting] = useState(null);
  const [allTx, setAllTx] = useState([]);
  const [tab, setTab] = useState("players");

  const load = () => {
    api.get("/players?limit=500").then(({ data }) => setPlayers(data));
    api.get("/admin/wallet/transactions?limit=100").then(({ data }) => setAllTx(data));
  };
  useEffect(() => { load(); }, []);

  const filtered = players.filter((p) => p.nickname.toLowerCase().includes(q.toLowerCase()) || p.username.includes(q.toLowerCase()));

  return (
    <div className="p-6 lg:p-8">
      <h1 className="font-display font-bold text-3xl mb-1 flex items-center gap-3"><Wallet className="text-amber-300" /> Ví tiền</h1>
      <p className="text-white/40 text-sm mb-6">Quản lý số dư & giao dịch ví của người chơi</p>

      <div className="flex gap-2 mb-5">
        {[["players", "Người chơi"], ["transactions", "Lịch sử giao dịch"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-md text-sm ${tab === k ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/40" : "bg-white/[0.03] text-white/60 border border-white/8"}`}>{l}</button>
        ))}
      </div>

      {tab === "players" ? (
        <>
          <div className="relative mb-4">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm người chơi..." className="w-full bg-[#0c0d14] border border-white/10 rounded-md pl-9 pr-4 py-2.5 text-sm" />
          </div>
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 overflow-hidden">
            {filtered.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-5 py-3 border-b border-white/5">
                <img src={p.avatar_url} alt="" className="w-9 h-9 rounded-full" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{p.nickname}</div>
                  <div className="text-xs text-white/40">@{p.username}</div>
                </div>
                <div className="font-display font-bold text-amber-300 text-base w-32 text-right" data-testid={`wallet-${p.username}`}>{formatVND(p.coin)}</div>
                <button onClick={() => setAdjusting(p)} data-testid={`adjust-${p.username}`} className="px-3 py-1.5 rounded text-xs bg-amber-400/10 text-amber-300 border border-amber-400/40">Điều chỉnh</button>
              </div>
            ))}
            {filtered.length === 0 && <div className="py-12 text-center text-white/40 text-sm">Không có người chơi</div>}
          </div>
        </>
      ) : (
        <div className="rounded-xl bg-[#0c0d14] border border-white/8 overflow-hidden">
          {allTx.length === 0 ? <div className="py-12 text-center text-white/40 text-sm">Chưa có giao dịch</div> : allTx.map((t) => {
            const player = players.find((p) => p.id === t.user_id);
            return (
              <div key={t.id} className="flex items-center gap-3 p-4 border-b border-white/5">
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{player?.nickname || t.user_id} <span className="text-white/40">· {TYPES.find((x) => x.v === t.type)?.l || t.type}</span></div>
                  <div className="text-xs text-white/40">{t.description || "—"} · {new Date(t.created_at).toLocaleString("vi-VN")}</div>
                </div>
                <div className={`font-display font-bold ${t.amount_vnd >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                  {t.amount_vnd >= 0 ? "+" : ""}{formatVND(t.amount_vnd)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {adjusting && <AdjustModal player={adjusting} onClose={() => setAdjusting(null)} onDone={() => { setAdjusting(null); load(); }} />}
    </div>
  );
}

const AdjustModal = ({ player, onClose, onDone }) => {
  const [amount, setAmount] = useState(0);
  const [type, setType] = useState("deposit");
  const [desc, setDesc] = useState("");
  const submit = async () => {
    if (!amount) return toast.error("Nhập số tiền");
    try {
      await api.post(`/admin/players/${player.id}/wallet`, { amount: Number(amount), type, description: desc });
      toast.success("Đã cập nhật ví");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0c0d14] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold">Ví: {player.nickname}</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="text-xs text-white/60 mb-3">Số dư hiện tại: <span className="text-amber-300 font-display font-bold">{formatVND(player.coin)}</span></div>
        <label className="block mb-3">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Số tiền (âm để trừ)</span>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="wallet-amount" className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm" />
          <div className="grid grid-cols-4 gap-1.5 mt-2">
            {PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setAmount(p)} className={`py-1 rounded text-[11px] ${p > 0 ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" : "bg-red-500/10 text-red-300 border border-red-500/20"}`}>
                {p > 0 ? "+" : ""}{p / 1000}k
              </button>
            ))}
          </div>
        </label>
        <label className="block mb-3">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Loại giao dịch</span>
          <select value={type} onChange={(e) => setType(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </label>
        <label className="block mb-4">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Mô tả (tùy chọn)</span>
          <input value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm" />
        </label>
        <button onClick={submit} data-testid="wallet-submit" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 text-black">Áp dụng</button>
      </div>
    </div>
  );
};
